import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, type TenantDetail } from '../lib/api';

/** Cloudflare NS-delegation card.
 *
 * Four visual states keyed off `tenant.ns_status`:
 *  - `not_started` → big "Set up Cloudflare DNS" CTA. One click creates
 *    the zone and reveals the NS pair.
 *  - `pending` → shows the NS pair to copy into the registrar, plus a
 *    "Check now" button (rate-limited 60s server-side). The rest of
 *    the M365 pipeline is blocked until this flips active.
 *  - `active` → green confirmation. The pipeline has already been
 *    enqueued by the worker so there's nothing the operator needs to do.
 *  - `timeout` / `failed` → red error with retry CTA.
 */
export default function CfDnsCard({ tenant }: { tenant: TenantDetail }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<'create' | 'check' | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const ns = tenant.cf_nameservers || [];
  const status = tenant.ns_status || 'not_started';

  async function handleCreate() {
    setErr(null); setBusy('create');
    try {
      await api.createCfZone(tenant.id);
      qc.invalidateQueries({ queryKey: ['tenant', tenant.id] });
    } catch (e: any) {
      setErr(e?.message || 'failed to create zone');
    } finally { setBusy(null); }
  }

  async function handleCheck() {
    setErr(null); setBusy('check');
    try {
      // /cf-zone/check now runs inline — the response reflects the
      // latest CF state, so invalidate immediately.
      await api.checkCfZone(tenant.id);
      await qc.invalidateQueries({ queryKey: ['tenant', tenant.id] });
    } catch (e: any) {
      setErr(e?.message || 'check failed');
    } finally { setBusy(null); }
  }

  if (status === 'not_started') {
    return (
      <div className="card p-5">
        <div className="text-xs uppercase tracking-wider text-dim mb-1">DNS delegation</div>
        <div className="text-sm text-ink mb-3">
          Point <span className="font-mono">{tenant.target_domain}</span>'s nameservers at Sendepot's Cloudflare so we can manage MX, SPF, DKIM, and DMARC for you. Provisioning starts automatically once delegation is confirmed.
        </div>
        <button onClick={handleCreate} disabled={busy !== null} className="btn-primary disabled:opacity-50">
          {busy === 'create' ? 'Creating zone…' : 'Set up Cloudflare DNS'}
        </button>
        {err && <div className="mt-2 text-xs text-err">{err}</div>}
      </div>
    );
  }

  if (status === 'active') {
    return (
      <div className="card p-5">
        <div className="text-xs uppercase tracking-wider text-dim mb-1">DNS delegation</div>
        <div className="text-sm text-ok flex items-center gap-2">
          <span className="inline-block h-2 w-2 rounded-full bg-ok" />
          Active — Cloudflare is authoritative for {tenant.target_domain}. The rest of the pipeline is queued.
        </div>
      </div>
    );
  }

  if (status === 'timeout' || status === 'failed') {
    return (
      <div className="card p-5 border border-err/30">
        <div className="text-xs uppercase tracking-wider text-dim mb-1">DNS delegation</div>
        <div className="text-sm text-err mb-3">
          {status === 'timeout'
            ? 'Delegation didn\'t confirm within 3 days. Update the nameservers at your registrar and try again.'
            : 'Cloudflare zone setup failed.'}
        </div>
        {ns.length > 0 && <NsList ns={ns} />}
        <button onClick={handleCheck} disabled={busy !== null} className="btn-primary mt-3 disabled:opacity-50">
          {busy === 'check' ? 'Checking…' : 'Re-check now'}
        </button>
        {err && <div className="mt-2 text-xs text-err">{err}</div>}
      </div>
    );
  }

  // pending
  return (
    <div className="card p-5">
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-xs uppercase tracking-wider text-dim">DNS delegation — pending</div>
        <span className="text-xs text-warn">Awaiting confirmation</span>
      </div>
      <div className="text-sm text-ink mb-3">
        At your domain registrar (GoDaddy, Namecheap, etc.), replace the existing nameservers for <span className="font-mono">{tenant.target_domain}</span> with the two below. Status will update automatically once the change is confirmed.
      </div>
      <NsList ns={ns} />
      <div className="mt-3 flex items-center gap-2">
        <button onClick={handleCheck} disabled={busy !== null} className="btn-primary disabled:opacity-50">
          {busy === 'check' ? 'Checking…' : 'Check now'}
        </button>
        <span className="text-[11px] text-dim">Rate-limited to 1 check/minute.</span>
      </div>
      {err && <div className="mt-2 text-xs text-err">{err}</div>}
    </div>
  );
}

function NsList({ ns }: { ns: string[] }) {
  return (
    <div className="space-y-2">
      {ns.map((host) => (
        <div key={host} className="flex items-center justify-between gap-3 rounded-md bg-white/[0.03] px-3 py-2 font-mono text-sm">
          <span className="truncate">{host}</span>
          <button
            onClick={() => { navigator.clipboard?.writeText(host); }}
            className="text-xs text-dim hover:text-ink"
            title="Copy"
          >
            Copy
          </button>
        </div>
      ))}
    </div>
  );
}
